import { Edit } from 'lucide-react';
import { PurchaseOrderLine } from '../../lib/api';
import { ChangelogEntry } from '../common/Changelog';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import ChangeRequestCard from './ChangeRequestCard';
import ActiveChangeRequestCard from './ActiveChangeRequestCard';
import LegacyChangeHistory from './LegacyChangeHistory';
import { groupChangeRequests, useChangeRequestAttachments } from './changeRequestGrouping';
import {
  FIELD_LABELS,
  changeRequestEntries,
} from './changeRequestsTabHelpers';
import { hasOpenChangeRequest, mayReviewProposal } from './pendingProposal';

interface ChangeRequestsTabProps {
  line: PurchaseOrderLine;
  changelogs: ChangelogEntry[];
  userType: 'customer' | 'supplier';
  onUpdate?: () => void;
}

export default function ChangeRequestsTab({ line, changelogs, userType, onUpdate }: ChangeRequestsTabProps) {
  const { formatDate: contextFormatDate, formatDateTime: contextFormatDateTime } = useFormatters();
  const { hasPurchasingWriteAccess } = useUserPermissions();

  // Customer review 11 Sep (item 28): an open change request needs the
  // workflow to say one is pending — see hasOpenChangeRequest. The CR-4688
  // fallback that opened this card on proposed values alone (or on
  // supplier_promise_date !== delivery_date) put a phantom "Active Change
  // Request … Unknown" with live Accept / Reject on every ERP-imported line,
  // where both are ordinary imported data.
  const hasProposalStatus = hasOpenChangeRequest(line);
  // CR-7309: CR-4688's stale-data reading (any proposed value, or a promise
  // date drifted from the delivery date) is kept for what it was written for —
  // telling a change_proposed_* row that has something behind it from one that
  // has nothing — and is reachable ONLY where the status claims a change. On
  // any other status those columns are not a proposal: on ERP-imported lines
  // supplier_proposed_date is the legacy Review Date (CR-7294).
  const hasAnyProposedValue = hasProposalStatus && !!(line.supplier_proposed_date || line.supplier_proposed_unit_price || line.supplier_proposed_qty
    || (line.supplier_promise_date && line.customer_required_date && line.supplier_promise_date !== line.customer_required_date));
  // CR-4882: a change_proposed_* status with nothing proposed at all is stuck
  // legacy data (cancelled before the backend self-heal landed); the data
  // migration heals those rows and this keeps the card off them meanwhile.
  // CR-7277 keeps the card — without controls — where the status is backed
  // only by drift or a no-op, so the reviewer is told why nothing is offered.
  // Terminal statuses (CR-4688 round 3) never reach here: not change_proposed.
  const isStuckProposalStatus = hasProposalStatus && !hasAnyProposedValue;
  const hasOpenChange = hasProposalStatus && !isStuckProposalStatus;

  const canActOnChange = (): boolean => {
    const isCustomer = userType === 'customer';
    const isSupplier = userType === 'supplier';

    if (isCustomer && !hasPurchasingWriteAccess(line.customer_id)) return false;

    if (isCustomer && line.acknowledge_status === 'change_proposed_supplier') return true;
    if (isSupplier && line.acknowledge_status === 'change_proposed_customer') return true;
    // Customer review 11 Sep (item 28): the CR-4688 "stale proposal"
    // allowance (any proposed value ⇒ may act) is gone. It is what armed
    // Accept on imported lines, and Accept on a `new` line is the supplier's
    // order acknowledgement — never the customer's to give from here.
    //
    // Deliberately NOT hold-aware: this answers "which side of the table am I
    // on", and it also gates ATTACHMENT UPLOAD below, which PR 1 left open on a
    // held line along with messages and the critical flag. The hold belongs on
    // the ACTION, so it rides isReviewer instead.
    return false;
  };

  const changeEntries = changeRequestEntries(changelogs);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return contextFormatDate(dateString);
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    return contextFormatDateTime(dateString);
  };

  const getFieldLabel = (field: string) => FIELD_LABELS[field] || field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const hasNoChanges = !hasOpenChange && changeEntries.length === 0;

  // CR-4706: Proposer is derived strictly from acknowledge_status. A
  // hasAnyProposedValue fallback misidentifies the counterparty as proposer.
  const isProposer = (
    (userType === 'supplier' && line.acknowledge_status === 'change_proposed_supplier') ||
    (userType === 'customer' && line.acknowledge_status === 'change_proposed_customer')
  );
  // EXPEDITING-PARITY PR 2: every Accept / Reject on this tab hangs off
  // isReviewer, so the shared rule rides here — it refuses a held line (the
  // server has refused one since PR 1) and a phantom/no-op payload (CR-7277).
  const isReviewer = mayReviewProposal(line, userType === 'customer' ? 'customer'
    : userType === 'supplier' ? 'supplier' : null) && canActOnChange() && !isProposer;

  // CR-4865: split the changelog into Delivery-Appeal-style grouped cards
  // (rows with a change_request_ref) and legacy ungrouped rows. The
  // legacy Change History block further down handles the orphan entries.
  const { groups, orphans } = groupChangeRequests(changeEntries);
  const poLineId = (line as unknown as { id?: number }).id || 0;
  const { attachmentsByRef, refresh: refreshAttachments } = useChangeRequestAttachments(poLineId);
  const canUploadSide = canActOnChange() || isProposer;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Edit className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-lg">Change Requests</h3>
        {/* CR-6409: change REQUESTS, not change-log rows — the number on this
            tab's label. A request writes a row for its proposal and another
            for its decision, so the row count read 3 beside a label of 2. */}
        {groups.length > 0 && (
          <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-sm">
            {groups.length}
          </span>
        )}
      </div>

      {/* Empty State */}
      {hasNoChanges && (
        <div className="text-center py-12 border rounded-lg bg-muted/20">
          <Edit className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground font-medium">No changes found for this PO line</p>
          {/* Customer review 11 Sep (item 28): the header still reads
              "Waiting on Supplier" on a new line, so say what is owed. */}
          {/* CR-7408 rework (tester, 7 Oct): a held line awaits its release, not
              an acknowledgement — and nothing can be proposed while it is held
              (expediting_rules.assert_open_for_negotiation refuses it). */}
          {line.on_hold ? (
            <p className="text-sm text-muted-foreground mt-2">
              Nothing has been proposed. The line is on hold, so changes can be proposed once it is released.
            </p>
          ) : line.acknowledge_status === 'new' && (
            <p className="text-sm text-muted-foreground mt-2">
              Nothing has been proposed. The line is awaiting the supplier&apos;s acknowledgement.
            </p>
          )}
        </div>
      )}

      {/* Open Change Request */}
      {hasOpenChange && (
        <ActiveChangeRequestCard
          line={line}
          isProposer={isProposer}
          isReviewer={isReviewer}
          formatDate={formatDate}
          formatDateTime={formatDateTime}
          onUpdate={onUpdate}
        />
      )}

      {/* CR-4865: Grouped change-request cards (Delivery-Appeal-style).
          Rows carrying change_request_ref group into one card; older rows
          without a ref fall through to the legacy Change History list below. */}
      {groups.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Change Requests ({groups.length})</h4>
          {groups.map(group => (
            <ChangeRequestCard
              key={group.ref}
              group={{ ...group, poLineId: poLineId }}
              attachments={attachmentsByRef.get(group.ref) || []}
              canUploadSubmission={canUploadSide}
              canUploadDecision={canUploadSide}
              onAttachmentsChanged={refreshAttachments}
              formatDateTime={formatDateTime}
              odd={line.odd_date ?? line.customer_required_date}
              otifWindow={line.supplier_otif_window}
            />
          ))}
        </div>
      )}

      <LegacyChangeHistory
        entries={orphans}
        odd={line.odd_date ?? line.customer_required_date}
        otifWindow={line.supplier_otif_window}
        getFieldLabel={getFieldLabel}
        formatDateTime={formatDateTime}
      />
    </div>
  );
}
